/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector, getFormValues } from 'redux-form';
import SelectField from '../../shared-components/reduxForms/fields/SelectField/SelectField';
import { connect } from 'react-redux';
import Button from '../../elements-buttons/Button';
import validateBillingAddressForm from './ValidateBillingAddressForm';
import onSubmitBillingAddress from './onSubmitBillingAddressForm';
import { get, filter, isEmpty, map, mapKeys } from 'lodash';
import RequiredLabel from '../../elements-inputs/RequiredLabel';
import TextField from '../../app-main/components/reduxForms/fields/TextField/TextField';
import { Tooltip } from '../../elements-tooltip/tooltip-v2';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import AddressFormInputs from './AddressFormInputs';

const BillingAddressForm = ({
  error,
  handleSubmit,
  defaultCountry,
  country,
  allCountries,
  isLoading,
  hasBillingAddress
}) => {
  const countryOptions = map(allCountries, country => {
    return { value: country.code, label: country.name };
  });

  const selectedCountry = filter(allCountries, ['code', country])[0];
  const keyMap = { code: 'value', name: 'label' };
  const divisions = get(selectedCountry, 'zones', []);
  const formattedDivisions = divisions.map(division => mapKeys(division, (value, key) => keyMap[key]));

  return (
    <form className="AddressForm ps-row-nested" onSubmit={handleSubmit}>
      <div className="col-md-3" />
      <div className="col-md-6">
        <h4><FormattedMessage id="hosting.billing_address_form.address_heading" defaultMessage="Billing Address"/></h4>
        <p><FormattedMessage id="hosting.billing_address_form.address_text" defaultMessage="This will be used when processing your payouts." /></p>
        <div>
          <AddressFormInputs
            defaultCountry={defaultCountry}
            countryOptions={countryOptions}
            selectedCountry={selectedCountry}
            formattedDivisions={formattedDivisions}
          />
        </div>

        <div>
          <Button
            category="primary"
            className="submit-cta block-xs-left full-width"
            disabled={isLoading}
            attributes={{ 'data-testing-id': 'address' }}
            isLoading={isLoading}
          >
            { hasBillingAddress ? <FormattedMessage id="hosting.billing_address_form.button_text_update" defaultMessage="Update billing address"/> : <FormattedMessage id="hosting.billing_address_form.button_text_add" defaultMessage="Add Billing Address"/>}
          </Button>
          <div>
            <a className="btn btn-gray-outline full-width margin-lg-top" href='/account/methods'>
              <FormattedMessage
                id="hosting.billing_address_form.form_cancel_link_text"
                defaultMessage="Cancel"
              />
            </a>
          </div>
        </div>
        <div className="text-danger text-small margin-lg-top">{error}</div>
      </div>
    </form>
  );
};

BillingAddressForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  defaultCountry: PropTypes.string,
  country: PropTypes.string,
  isPosting: PropTypes.bool,
  isEdit: PropTypes.bool,
  envCountries: PropTypes.object,
  allCountries: PropTypes.array,
  intl: intlShape,
  isBillingAddressDifferent: PropTypes.bool,
  initialValues: PropTypes.object,
  hasBillingAddress: PropTypes.bool,
  isLoading: PropTypes.bool
};

const config = {
  form: 'BILLING_ADDRESS_FORM',
  validate: validateBillingAddressForm,
  onSubmit: onSubmitBillingAddress,
  enableReinitialize: true
};

const selector = formValueSelector('BILLING_ADDRESS_FORM');

const mapStateToProps = (state) => {
  const defaultCountry = get(state, 'space.countryStatus.data.country');
  const billingCountry = get(state, 'fee_profiles.billingAddress.data.0.country');
  const country = selector(state, 'country') || defaultCountry || billingCountry;
  const fee_profiles = get(state, 'fee_profiles');
  const envCountries = get(state, 'env.web.countries');
  const updateError = get(fee_profiles, 'billingAddress.updateError');
  const billingInfo = get(fee_profiles, 'billingAddress.data', []);

  // billingInfo is undefined when there is a post/update error
  // also check for updateError to display the correct button text
  const hasBillingAddress = !(isEmpty(billingInfo)) || updateError;

  // conditions for loading button
  const isPosting = get(fee_profiles, 'billingAddress.isPosting');
  const isFetching = get(fee_profiles, 'billingAddress.isFetching');
  const hasPosted = get(fee_profiles, 'billingAddress.hasPosted');
  const isLoading = isPosting || isFetching || hasPosted;

  const ssoId = get(state, 'profile.SSOid');

  // this is for preventing form reset onSubmit
  const billingAddress = billingInfo[0] || billingInfo.body;

  return {
    isLoading,
    country,
    defaultCountry,
    envCountries,
    hasBillingAddress,
    initialValues: {
      ...billingAddress,
      ssoId,
      country: country,
      type: 'BILLING',
      obj_state: 'ACTIVE'
    }
  };
};

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(BillingAddressForm)));
